import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useResetRecoilState, useSetRecoilState } from 'recoil';

import isEmpty from 'lodash-es/isEmpty';

import { pageBackPathState, pageTitleState, pageTooltipState } from '@bo-recoil/common/atom';

const useContentsLayout = (props = {}, add = false) => {
  const { title, backPath, tooltip } = props;

  const setPageTitle = useSetRecoilState(pageTitleState);
  const resetPageTitle = useResetRecoilState(pageTitleState);
  const setPageBackPath = useSetRecoilState(pageBackPathState);
  const resetPageBackPath = useResetRecoilState(pageBackPathState);
  const setPageTooltip = useSetRecoilState(pageTooltipState);
  const resetPageTooltip = useResetRecoilState(pageTooltipState);

  const { id } = useParams();
  const { pathname, state } = useLocation();
  const navigate = useNavigate();

  const isAdd = useMemo(() => add || id === 'add', [id, add]);
  const defaultBackPath = useMemo(() => pathname.substring(0, pathname.lastIndexOf(`/${id}`)), [id, pathname]);

  useEffect(() => {
    if (typeof title === 'string') {
      setPageTitle(title);
      return () => resetPageTitle();
    } else if (title !== undefined) {
      setPageTitle(isAdd ? title?.add : title?.detail);
      return () => resetPageTitle();
    }
  }, [isAdd, title, setPageTitle, resetPageTitle]);

  useEffect(() => {
    setPageTooltip(tooltip);

    return () => resetPageTooltip();
  }, [resetPageTooltip, setPageTooltip, tooltip]);

  useEffect(() => {
    setPageBackPath(backPath ? backPath : defaultBackPath);

    return () => resetPageBackPath();
  }, [defaultBackPath, backPath, setPageBackPath, resetPageBackPath]);

  const navigateWithSearchOption = useCallback(
    (url, lastSearchOption, gridRef, moreState = {}, windowOpen) => {
      const searchOption = {};
      lastSearchOption && (searchOption.lastSearchForm = { ...lastSearchOption });
      if (gridRef?.current) {
        searchOption.lastSearchPageable = {};
        searchOption.lastSearchPageable.page = gridRef?.current?.getPageIndex();
        searchOption.lastSearchPageable.size = gridRef?.current?.getPageSize();
        searchOption.lastSearchPageable.sort = gridRef?.current?.getPageSort();
      }

      if(windowOpen) {
        window.open(url);
      } else {
        // if(newWindow && Object.keys(moreState).length === 0 && url.indexOf('/add') === -1) {
        //   console.log("if");
        //   window.open(url);
        // } else {
          navigate(url, {state: {searchOption, ...moreState}});
        // }
      }
    },
    [navigate]
  );

  const laySearchOption = useCallback(
    (setSearchOption, gridRef) => {
      if (isEmpty(state)) return false;

      const { searchOption, ...restState } = state;
      if (!searchOption?.url?.startsWith?.(pathname) && !searchOption?.lastSearchForm?.forcedSustain) return false;

      const layGrid = (lastSearchPageable) => {
        gridRef?.current?.setPageIndex(lastSearchPageable.page || 0);
        gridRef?.current?.setPageSize(lastSearchPageable.size || 100);
        gridRef?.current?.setPageSort(lastSearchPageable.sort || '');
      };

      if (!searchOption?.lastSearchPageable && !searchOption?.lastSearchForm) return false;
      if (searchOption?.lastSearchPageable) layGrid(searchOption?.lastSearchPageable);
      if (searchOption?.lastSearchForm) setSearchOption({ ...searchOption.lastSearchForm });

      window.history.replaceState({ ...restState }, '');
      return true;
    },
    [pathname, state]
  );

  return {
    isAdd,
    state,
    defaultBackPath,
    navigateWithSearchOption,
    laySearchOption,
  };
};

export default useContentsLayout;
